import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Alert,
    CircularProgress,
    TextField,
    InputAdornment,
    TableSortLabel,
    TablePagination,
    Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { StyledTableContainer } from '../StyledTable';

export interface Column<T> {
    id: keyof T | string;
    label: string;
    numeric?: boolean;
    width?: number;
    sortable?: boolean;
    render?: (row: T) => React.ReactNode;
}

export interface TableProps<T> {
    title?: string;
    columns: Column<T>[];
    data: T[];
    loading?: boolean;
    error?: string | null;
    orderBy?: string;
    order?: 'asc' | 'desc';
    page?: number;
    rowsPerPage?: number;
    totalCount?: number;
    searchQuery?: string;
    searchPlaceholder?: string;
    showSearch?: boolean;
    showPagination?: boolean;
    emptyStateMessage?: string;
    headerContent?: React.ReactNode;
    expandedContent?: (row: T) => React.ReactNode;
    onSearchChange?: (query: string) => void;
    onRequestSort?: (property: string) => void;
    onPageChange?: (newPage: number) => void;
    onRowsPerPageChange?: (newRowsPerPage: number) => void;
    onRowClick?: (row: T) => void;
    getRowId: (row: T) => string | number;
}

export function SharedTable<T>({
    title,
    columns,
    data,
    loading = false,
    error = null,
    orderBy,
    order = 'asc',
    page = 0,
    rowsPerPage = 10,
    totalCount,
    searchQuery = '',
    searchPlaceholder = 'Search...',
    showSearch = true,
    showPagination = true,
    emptyStateMessage = 'No data found',
    headerContent,
    expandedContent,
    onSearchChange,
    onRequestSort,
    onPageChange,
    onRowsPerPageChange,
    onRowClick,
    getRowId,
}: TableProps<T>) {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="shared-table">
            {(title || headerContent) && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    {title && <Typography variant="h5" sx={{ fontWeight: 500 }}>{title}</Typography>}
                    {headerContent}
                </Stack>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {showSearch && onSearchChange && (
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                        size="small"
                    />
                </Box>
            )}

            <Paper sx={{ width: '100%', mb: 2, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                <StyledTableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id.toString()}
                                        align={column.numeric ? 'right' : 'left'}
                                        width={column.width}
                                    >
                                        {column.sortable && onRequestSort ? (
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={() => onRequestSort(column.id.toString())}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row) => (
                                <React.Fragment key={getRowId(row)}>
                                    <TableRow
                                        hover={!!onRowClick}
                                        onClick={() => onRowClick?.(row)}
                                        sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                    >
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.id.toString()}
                                                align={column.numeric ? 'right' : 'left'}
                                            >
                                                {column.render
                                                    ? column.render(row)
                                                    : (row[column.id as keyof T] as React.ReactNode)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    {expandedContent && (
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
                                                {expandedContent(row)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                            {data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            {emptyStateMessage}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
                {showPagination && onPageChange && onRowsPerPageChange && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalCount || data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => onPageChange(newPage)}
                        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
                    />
                )}
            </Paper>
        </div>
    );
} 